import { createTheme, type ThemeOptions } from "@mui/material/styles";

/**
 * Tokens deliberately matched to the sibling Haystack Bug Tracker app (a
 * Tailwind + CSS-custom-property system, not MUI) so the two apps feel like
 * one product family. Font stack and hex values below are copied from its
 * `tailwind.config.js` / `src/styles/theme.css` - keep these two in sync by
 * hand if that project's palette ever changes.
 */
const sharedOptions: ThemeOptions = {
  shape: {
    borderRadius: 8,
  },
  typography: {
    fontFamily: ["Inter", "SF Pro Text", "system-ui", "-apple-system", "sans-serif"].join(","),
    fontSize: 14,
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
        },
      },
    },
  },
};

export const lightTheme = createTheme({
  ...sharedOptions,
  palette: {
    mode: "light",
    primary: { main: "#3b6bda", dark: "#2d5bc4", light: "#ebf1fd", contrastText: "#ffffff" },
    secondary: { main: "#6b7280" },
    error: { main: "#dc2626", light: "#fef2f2", dark: "#b91c1c" },
    success: { main: "#16a34a", light: "#f0fdf4" },
    warning: { main: "#d97706", light: "#fffbeb" },
    background: { default: "#f6f7f9", paper: "#ffffff" },
    text: { primary: "#111827", secondary: "#6b7280", disabled: "#9ca3af" },
    divider: "#e5e7eb",
  },
});

export const darkTheme = createTheme({
  ...sharedOptions,
  palette: {
    mode: "dark",
    primary: { main: "#5b8dee", dark: "#4a7ee3", light: "#1a2340", contrastText: "#ffffff" },
    secondary: { main: "#8892a4" },
    error: { main: "#f87171", light: "#2d1515", dark: "#ef4444" },
    success: { main: "#4ade80", light: "#0f2a1a" },
    warning: { main: "#fbbf24", light: "#2a1f08" },
    background: { default: "#0f1117", paper: "#1a1d27" },
    text: { primary: "#e8eaf0", secondary: "#8892a4", disabled: "#5c6478" },
    divider: "#2d3348",
  },
});

export type ThemeMode = "light" | "dark";

export function getTheme(mode: ThemeMode) {
  return mode === "dark" ? darkTheme : lightTheme;
}
